import React, { useEffect, useMemo, useState } from 'react';
import { parseWorkshopStart } from '../lib/format';
import { Timer } from 'lucide-react';

const UNITS = [
  { key: 'days', label: 'Days' },
  { key: 'hours', label: 'Hours' },
  { key: 'minutes', label: 'Mins' },
  { key: 'seconds', label: 'Secs' },
];

/** Time left until `target`, or null once it has passed. */
function remaining(target) {
  const ms = target.getTime() - Date.now();
  if (ms <= 0) return null;

  const total = Math.floor(ms / 1000);
  return {
    days: Math.floor(total / 86400),
    hours: Math.floor((total % 86400) / 3600),
    minutes: Math.floor((total % 3600) / 60),
    seconds: total % 60,
  };
}

/**
 * One unit's digits.
 *
 * Each character is keyed by its position *and* its value, so a digit that
 * changes is a different element to React — it unmounts the old one and mounts
 * a new one, and the mount animation rolls it in from above. A digit that did
 * not change keeps its identity and stays perfectly still, which is the whole
 * trick: only the seconds move every second.
 */
function Digits({ value }) {
  const text = String(value).padStart(2, '0');

  return (
    <span className="count-digits" aria-hidden="true">
      {text.split('').map((character, position) => (
        <span key={`${position}-${character}`} className="count-digit">
          {character}
        </span>
      ))}
    </span>
  );
}

/**
 * Countdown to the first live session.
 *
 * The single most persuasive fact on the page is that this thing has a start
 * time and it is coming. A date reads as information; a clock running down
 * reads as a reason to book now.
 */
export const CountdownTimer = ({ date, time }) => {
  const target = useMemo(() => parseWorkshopStart(date, time), [date, time]);
  const [left, setLeft] = useState(() => (target ? remaining(target) : null));

  useEffect(() => {
    if (!target) return undefined;

    setLeft(remaining(target));
    const id = setInterval(() => setLeft(remaining(target)), 1000);
    return () => clearInterval(id);
  }, [target]);

  if (!target) return null;

  // Already started. Saying so beats a row of zeros, and beats hiding the
  // block entirely — the batch being live is itself a reason to enquire.
  if (!left) {
    return (
      <div className="mt-6 rounded-2xl glass-dark px-4 py-3.5 flex items-center gap-2.5">
        <span className="relative flex w-2 h-2 shrink-0">
          <span className="absolute inset-0 rounded-full bg-brand-500 animate-ping" />
          <span className="relative w-2 h-2 rounded-full bg-brand-500" />
        </span>
        <p className="text-sm font-bold">This batch has started</p>
        <p className="text-[11px] text-white/45 ml-auto">Ask us about the next one</p>
      </div>
    );
  }

  // Under a day left, the clock stops being a schedule and starts being a
  // deadline. Colour it like one.
  const urgent = left.days === 0;

  return (
    <div className={`countdown mt-6 ${urgent ? 'countdown--urgent' : ''}`}>
      <p className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.16em] text-white/45">
        <Timer className="w-3.5 h-3.5 text-gold-400" />
        Starts in
      </p>

      <div className="mt-2.5 grid grid-cols-4 gap-2">
        {UNITS.map(({ key, label }) => (
          <div key={key} className="count-cell">
            <Digits value={left[key]} />
            <span className="count-label">{label}</span>
          </div>
        ))}
      </div>

      {/* One accessible reading, updated on the minute rather than the second —
          a screen reader announcing a new value every second is unusable. */}
      <p className="sr-only" aria-live="polite">
        {`Starts in ${left.days} days, ${left.hours} hours and ${left.minutes} minutes`}
      </p>
    </div>
  );
};
